import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Dialog,
  Field,
  HStack,
  Portal,
  Table,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Button as ButtonComponent, Textarea } from '@themes/aegix';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toaster } from '../../ui/toaster';
import {
  importSamlMappings,
  importScimMappings,
  type ImportMappingsPreview,
} from '../../../services';

/**
 * CSV mapping import dialog — Phase 4 of the ADR 019 self-service
 * surface.
 *
 * Two-step flow:
 *
 *   1. **Preview**: admin pastes/uploads CSV and clicks Preview. The
 *      backend validates each row against known roles + sites in their
 *      district and returns either the parsed rows OR a per-row error
 *      list, both rendered inline.
 *   2. **Confirm**: clicking *Replace mappings* re-posts the same body
 *      without `dry_run` to actually persist. The replace is destructive
 *      — existing role/site mappings on the SCIMConfig are overwritten.
 *
 * CSV columns: ``idp_group`` (required), ``tiaki_role``, ``tiaki_school``.
 * Header row required; UTF-8 with optional BOM (so Excel exports work).
 */

export type MappingCsvImportMode = 'scim' | 'saml';

export interface MappingCsvImportProps {
  /**
   * Which CSV-import endpoint to target. `scim` writes to
   * SCIMConfiguration.role_mappings/school_mappings JSON;
   * `saml` writes to SAMLRoleConfig/SAMLSchoolConfig rows.
   */
  mode: MappingCsvImportMode;
  configId: number;
  open: boolean;
  onClose: () => void;
}

interface PerRowError {
  line: number;
  error: string;
}

export function MappingCsvImport({
  mode,
  configId,
  open,
  onClose,
}: MappingCsvImportProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [csvText, setCsvText] = useState<string>(
    'idp_group,tiaki_role,tiaki_school\n',
  );
  const [csvFileName, setCsvFileName] = useState<string | null>(null);
  const [preview, setPreview] = useState<ImportMappingsPreview | null>(null);
  const [perRowErrors, setPerRowErrors] = useState<PerRowError[] | null>(null);
  const [topLevelError, setTopLevelError] = useState<string | null>(null);

  const importFn = mode === 'saml' ? importSamlMappings : importScimMappings;

  const reset = () => {
    setPreview(null);
    setPerRowErrors(null);
    setTopLevelError(null);
  };

  const previewMutation = useMutation({
    mutationFn: () => importFn(configId, csvText, true),
    onSuccess: (data) => {
      reset();
      setPreview(data);
    },
    onError: (err) => {
      reset();
      // The backend returns 400 with either {detail, errors:[{line, error}]}
      // or just {detail}. ApiClient throws Error(message) so we re-parse
      // by hitting the request directly only on errors — for simplicity,
      // try to extract from err.message.
      const message = (err as Error).message || 'CSV validation failed.';
      // Best-effort extraction: the backend's exception handler usually
      // serializes errors into the top-level Error.message JSON.
      try {
        const parsed = JSON.parse(message);
        if (parsed.errors) {
          setPerRowErrors(parsed.errors as PerRowError[]);
          setTopLevelError(parsed.detail || 'CSV validation failed.');
          return;
        }
        if (parsed.detail) {
          setTopLevelError(parsed.detail);
          return;
        }
      } catch {
        /* fall through — not JSON */
      }
      setTopLevelError(message);
    },
  });

  const replaceMutation = useMutation({
    mutationFn: () => importFn(configId, csvText, false),
    onSuccess: () => {
      const queryKey =
        mode === 'saml'
          ? ['sso', 'saml-configs', configId]
          : ['sso', 'scim-configs', configId];
      queryClient.invalidateQueries({ queryKey });
      toaster.create({
        title: 'Mappings replaced',
        description:
          mode === 'saml'
            ? 'SAML role and site mappings updated from the CSV.'
            : 'Inline role and site mappings updated from the CSV.',
        type: 'success',
        duration: 5000,
      });
      onClose();
      setPreview(null);
    },
    onError: (err) => {
      setTopLevelError((err as Error).message);
    },
  });

  const handleClose = () => {
    setCsvText('idp_group,tiaki_role,tiaki_school\n');
    setCsvFileName(null);
    reset();
    onClose();
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={({ open: isOpen }) => {
        if (!isOpen) handleClose();
      }}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content
            maxW="800px"
            data-e2e="sso-csv-import-dialog"
          >
            <Dialog.Header>
              <Dialog.Title>Import mappings from CSV</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body>
              <VStack align="stretch" gap={4}>
                <Text fontSize="sm" color="gray.700">
                  Paste a CSV with a header row. Recognized columns:
                  <Box as="code" bg="gray.100" px={1} mx={1}>
                    idp_group
                  </Box>
                  (required),
                  <Box as="code" bg="gray.100" px={1} mx={1}>
                    tiaki_role
                  </Box>
                  ,
                  <Box as="code" bg="gray.100" px={1} mx={1}>
                    tiaki_school
                  </Box>
                  . Existing inline mappings will be replaced on confirm.
                </Text>

                <Field.Root>
                  <Field.Label htmlFor="csv-import-textarea">
                    CSV body
                  </Field.Label>
                  <Textarea
                    id="csv-import-textarea"
                    data-e2e="sso-csv-import-textarea"
                    fontFamily="mono"
                    fontSize="xs"
                    rows={8}
                    value={csvText}
                    onChange={(e) => {
                      setCsvText(e.target.value);
                      setCsvFileName(null);
                      reset();
                    }}
                  />
                </Field.Root>

                <Field.Root>
                  <Field.Label htmlFor="csv-import-file">
                    {t('sso.orUploadCsvFile', '…or upload a .csv file')}
                  </Field.Label>
                  <HStack gap={3}>
                    <ButtonComponent
                      type="button"
                      visual="secondary"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {csvFileName
                        ? t('common.changeFile', 'Change file')
                        : t('common.chooseCsvFile', 'Choose CSV file…')}
                    </ButtonComponent>
                    <Text
                      fontSize="sm"
                      color={csvFileName ? 'text.primary' : 'gray.500'}
                      wordBreak="break-all"
                    >
                      {csvFileName ?? t('common.noFileSelected', 'No file selected')}
                    </Text>
                  </HStack>
                  <input
                    ref={fileInputRef}
                    id="csv-import-file"
                    type="file"
                    accept=".csv,text/csv"
                    hidden
                    data-e2e="sso-csv-import-file"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      try {
                        const text = await file.text();
                        setCsvText(text);
                        setCsvFileName(file.name);
                        reset();
                      } catch (err) {
                        toaster.create({
                          title: 'File read failed',
                          description: (err as Error).message,
                          type: 'error',
                        });
                      }
                    }}
                  />
                </Field.Root>

                {topLevelError ? (
                  <Alert.Root status="error" data-e2e="sso-csv-import-error">
                    <Alert.Indicator />
                    <Alert.Content>
                      <Alert.Title>Import rejected</Alert.Title>
                      <Alert.Description>{topLevelError}</Alert.Description>
                    </Alert.Content>
                  </Alert.Root>
                ) : null}

                {perRowErrors && perRowErrors.length > 0 ? (
                  <Box
                    borderWidth="1px"
                    borderColor="red.200"
                    borderRadius="md"
                    p={3}
                    data-e2e="sso-csv-import-row-errors"
                  >
                    <Text fontSize="sm" fontWeight={600} mb={2} color="red.700">
                      Per-row errors ({perRowErrors.length})
                    </Text>
                    <Table.Root size="sm">
                      <Table.Header>
                        <Table.Row>
                          <Table.ColumnHeader>Line</Table.ColumnHeader>
                          <Table.ColumnHeader>Error</Table.ColumnHeader>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {perRowErrors.map((e) => (
                          <Table.Row key={`${e.line}-${e.error}`}>
                            <Table.Cell>{e.line}</Table.Cell>
                            <Table.Cell>{e.error}</Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table.Root>
                  </Box>
                ) : null}

                {preview ? (
                  <Box
                    borderWidth="1px"
                    borderColor="green.200"
                    borderRadius="md"
                    p={3}
                    data-e2e="sso-csv-import-preview"
                  >
                    <Text fontSize="sm" fontWeight={600} mb={2} color="green.700">
                      Preview ({preview.role_count} role mapping
                      {preview.role_count === 1 ? '' : 's'},{' '}
                      {preview.school_count} site mapping
                      {preview.school_count === 1 ? '' : 's'})
                    </Text>
                    <Table.Root size="sm">
                      <Table.Header>
                        <Table.Row>
                          <Table.ColumnHeader>IdP group</Table.ColumnHeader>
                          <Table.ColumnHeader>Tiaki role</Table.ColumnHeader>
                          <Table.ColumnHeader>Tiaki site</Table.ColumnHeader>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {[
                          ...preview.role_mappings.map((m) => ({
                            kind: 'role' as const,
                            ...m,
                          })),
                          ...preview.school_mappings.map((m) => ({
                            kind: 'school' as const,
                            ...m,
                          })),
                        ].map((row, idx) => (
                          <Table.Row key={idx}>
                            <Table.Cell>{row.group_name}</Table.Cell>
                            <Table.Cell>
                              {row.kind === 'role' ? row.role_name : '—'}
                            </Table.Cell>
                            <Table.Cell>
                              {row.kind === 'school' ? row.school_name : '—'}
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table.Root>
                  </Box>
                ) : null}
              </VStack>
            </Dialog.Body>
            <Dialog.Footer>
              <HStack gap={3}>
                <ButtonComponent visual="secondary" onClick={handleClose}>
                  Cancel
                </ButtonComponent>
                {preview ? (
                  <ButtonComponent
                    visual="alert"
                    onClick={() => replaceMutation.mutate()}
                    disabled={replaceMutation.isPending}
                    data-e2e="sso-csv-import-replace"
                  >
                    {replaceMutation.isPending
                      ? 'Replacing…'
                      : 'Replace mappings'}
                  </ButtonComponent>
                ) : (
                  <ButtonComponent
                    visual="primary"
                    onClick={() => previewMutation.mutate()}
                    disabled={previewMutation.isPending}
                    data-e2e="sso-csv-import-preview-button"
                  >
                    {previewMutation.isPending ? 'Previewing…' : 'Preview'}
                  </ButtonComponent>
                )}
              </HStack>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}

export default MappingCsvImport;
